/**
 * The app's brand colours, overlaid onto a Vuetify theme.
 *
 * Vendored from `metaship-frontend/shared/vue3-tenant-theme` with the fork (D28), where it existed so
 * a module could match whatever TENANT it was hosted by without a rebuild. It came across with three
 * more exports for that job — `readTenant()`, `withTenantTheme()` and a `TenantState` — which read
 * the tenant off the host global state. **No module ever imported them**, and D118 (PNK-121) removed
 * the state they read, so they went with it. What is left is the one function the shell itself calls
 * (`app/branding.ts`, at `createVuetify()` time) and the three types that describe a brand.
 *
 * Rules:
 * - `primary`, `secondary`, `tertiary` apply to BOTH dark and light themes
 *   (`tertiary` also fills Vuetify's `accent` slot).
 * - `background`, `surface`, `navbar` apply ONLY to the theme whose `dark` flag matches the brand's
 *   `defaultTheme` — a brand with a white background must not paint white surfaces into a person's
 *   dark mode. `surface` also fills the house aliases `card` and `foreground`; `navbar` also fills
 *   `toolbars`.
 */

export interface AppBrandingColours {
  primary: string | null;
  secondary: string | null;
  tertiary: string | null;
  background: string | null;
  surface: string | null;
  navbar: string | null;
}

/**
 * Whose identity the shell wears. `own` — the app stands alone under its own name, mark, favicon
 * and colours (both launch tenants, every white-label). `metaship` — the literal the interface keeps
 * for "the vendor's own product line", drawn as "Pinksy | <app>" here. Identity is about what is
 * DRAWN; `kind` still decides organisation pinning.
 */
export type BrandIdentity = "metaship" | "own";

export interface AppBranding {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  /** The full lockup — mark and wordmark — where there is room for it. A pair, like the mark: the
   *  sign-in page is themed. Null keeps the tile-and-name hero. */
  logoWideLightUrl: string | null;
  logoWideDarkUrl: string | null;
  iconUrl: string | null;
  colours: AppBrandingColours;
  defaultTheme: "dark" | "light";
  identity: BrandIdentity;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function hex(value: string | null | undefined): string | null {
  return typeof value === "string" && HEX.test(value.trim()) ? value.trim() : null;
}

/**
 * Overlay the brand's colours onto one theme's colour map. Pure — returns a new object.
 * `dark` is the theme's own flag; `defaultTheme` is the brand's.
 */
export function overlayBrandColours(
  colors: Record<string, string>,
  dark: boolean,
  branding: AppBranding,
): Record<string, string> {
  const c = branding.colours ?? ({} as AppBrandingColours);
  const out: Record<string, string> = { ...colors };

  const primary = hex(c.primary);
  const secondary = hex(c.secondary);
  const tertiary = hex(c.tertiary);
  if (primary) out.primary = primary;
  if (secondary) out.secondary = secondary;
  if (tertiary) out.accent = tertiary;

  const surfacesApply = (branding.defaultTheme === "dark") === dark;
  if (surfacesApply) {
    const background = hex(c.background);
    const surface = hex(c.surface);
    const navbar = hex(c.navbar);
    if (background) out.background = background;
    if (surface) {
      out.surface = surface;
      out.card = surface;
      out.foreground = surface;
    }
    if (navbar) {
      out.navbar = navbar;
      out.toolbars = navbar;
    }
  }
  return out;
}

/** WCAG 2 relative luminance of a `#RRGGBB`. */
function luminance(colour: string): number {
  const channel = (at: number): number => {
    const value = Number.parseInt(colour.slice(at, at + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG 2 contrast ratio between two `#RRGGBB` colours. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** The fills a label is drawn ON: each gets the `on-` colour that reads best on it. */
const FILLS = ["primary", "secondary", "tertiary", "accent", "error", "success", "warning", "info", "alert", "edit"];

/**
 * 🔴 **TEXT ON A FILL IS CHOSEN BY WCAG 2, NOT BY VUETIFY** (round 3). Left to itself Vuetify picks each
 * `on-` colour by APCA, and on Pinksy pink (#F3418A) it picked WHITE — 3.54:1, so every primary button
 * failed AA; #16191D is 4.98:1. Of `lightText` and `darkText` this takes the higher WCAG ratio, for every
 * fill the theme (or the brand overlay) has and that has no `on-` colour of its own. Pure.
 */
export function withReadableOnColours(colors: Record<string, string>, lightText: string, darkText: string): Record<string, string> {
  const out: Record<string, string> = { ...colors };
  for (const fill of FILLS) {
    const colour = hex(out[fill]);
    if (colour === null || out[`on-${fill}`] !== undefined) continue;
    out[`on-${fill}`] = contrastRatio(colour, lightText) >= contrastRatio(colour, darkText) ? lightText : darkText;
  }
  return out;
}
